<template>
    <div class="search__content rounded-3xl my-5 xl:pb-10 mx-3" v-if="companyCategories.length && !categoriesLoading">
        <ul class="search__tabs-list flex flex-wrap text-sm text-center">
            <li class="p-2 search__tabs" v-for="(category, idx) in companyCategories"
                :key="idx" :class="{
                    'search__tabs--active': category?._id === selectedCategory?._id,
                }" @click="switchCategory(category)">
                <button class="inline-block p-3 border-b-2 rounded-t-lg">
                    {{ category?.name || category[$i18n.locale] }}
                </button>
            </li>
        </ul>
        <div class="subcategory flex flex-col">
            <div class="subcategory__list grid lg:grid-cols-5 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 xl:gap-x-5 gap-x-3 xl:m-[25px] mx-[15px]">
                <div v-if="selectedCategorySubcategories?.length"
                     class="flex items-center my-3"
                     v-for="(subCategory, idx) in selectedCategorySubcategories"
                     :key="'subcategory' + idx">
                    <input type="checkbox"
                           :value="subCategory._id"
                           class="custom-checkbox custom-checkbox--small"
                           :id="`subcategory-checkbox_${subCategory._id}`"
                           v-model="selectedSubcategories">
                    <label :for="`subcategory-checkbox_${subCategory._id}`"
                           class="xl:text-sm text-xs  font-semibold">
                        {{ subCategory?.name || subCategory[$i18n.locale] }}
                    </label>
                </div>
                <div class="flex justify-center items-center my-3" v-else>
                    <p>{{ $t('NO_SUBCATEGORIES') }}</p>
                </div>
            </div>
            <div class="flex gap-5 md:mx-10 mx-3 my-5" v-if="!props.hideBottomActions">
                <button class="btn w-1/4"
                        style="background-color:#F6F6F6"
                        @click="resetSelection">
                    {{ $t('RESET') }}
                </button>
                <button class="btn subcategory__button w-3/4 xl:py-[15px] py-[8px]" type="button" @click="emit('search', {categoryId: selectedCategory, subcategoriesIds: selectedSubcategories})" :disabled="!selectedCategory || selectedSubcategories.length === 0">
                    {{ $t('SEARCH') }}
                </button>
            </div>
        </div>
    </div>
    <div v-else class="flex justify-center m-5">
        <loader/>
    </div>
</template>

<script setup lang="ts">
import {ref, watch, onMounted, nextTick} from 'vue';
import {storeToRefs} from 'pinia';
import Loader from "./Loader.vue";
import {useStore} from "../../stores/store";
import {AppEvents} from "../../types/events";

const { $listen } = useNuxtApp()
const store = useStore();
const {companyCategories, marketplaceOffers: offers} = storeToRefs(store);
const categoriesLoading = ref(false);

const emit = defineEmits(['update:category', 'update:subcategories', 'search']);
const props = defineProps({
        editMode: {
            type: Boolean,
            default: false
        },
        hideBottomActions: {
            type: Boolean,
            default: false
        }
        , category: {
            type: Object,
            default: () => ({})
        }, subcategories: {
            type: Set,
            default: ''
        },
        showFirstOnInit: {
            type: Boolean,
            default: false
        }
    }
);

const selectedCategory = computed({
    get() {
        return props.category;
    },
    set(value) { emit('update:category', value) }
})
const selectedSubcategories = computed({
    get() {
        return props.subcategories
    },
    set(value) { emit('update:subcategories', value) }
})


const selectedCategorySubcategories = computed(() => {
    return companyCategories.value.find(cat => cat._id === selectedCategory.value?._id)?.subcategories;
})

function switchCategory(category) {
    selectedCategory.value = companyCategories.value.find(c => c._id === category._id);
    selectedSubcategories.value = new Set([]);
}

function resetSelection() {
    selectedSubcategories.value = [];
}
function selectFirstCategoryAndServiceAndSearch() {
    if (!props.editMode) {
        switchCategory(companyCategories.value[0]);

        setTimeout(() => {
            selectedSubcategories.value = selectedCategorySubcategories.value?.map(sub => sub._id) || [];
        }, 100);

        switchSubcategory(null, true);
    }

    emit('search', {categoryId: selectedCategory.value, subcategoriesIds: selectedSubcategories.value})
}

$listen(AppEvents.MARKETPLACE_FETCH_OFFERS_INIT, () => {
    emit('search', {categoryId: selectedCategory.value, subcategoriesIds: selectedSubcategories.value})
})

function switchSubcategory(subCategory = null, switchToFirst = false) {
    if (switchToFirst) {
        nextTick().then(() => {
            selectedSubcategories.value = new Set([selectedCategory.value?.subcategories[0]?._id])
        })
        return
    }
    selectedSubcategories.value = new Set([subCategory._id])
}

onMounted(async () => {
    categoriesLoading.value = true;
    try {
        await store.fetchCompanyCategories();
    } finally {
        if (props.editMode && !selectedCategory.value && companyCategories.value.length > 0) {
            switchCategory(companyCategories.value[0]);
            selectedSubcategories.value = selectedCategorySubcategories.value?.map(sub => sub._id) || []
            selectFirstCategoryAndServiceAndSearch()
        }
        if (props.showFirstOnInit) {
            switchCategory(companyCategories.value[0]);
            selectedSubcategories.value = selectedCategorySubcategories.value?.map(sub => sub._id) || []
            selectFirstCategoryAndServiceAndSearch()
        }
        categoriesLoading.value = false;
    }
});
</script>

<style lang="scss" scoped>
.search {
  &__content {
    background: #fff;
    display: block;
    padding: 0;
    border: 1px solid #000;
  }
  &__tabs-list {
    padding-inline: 15px;
  }

  &__tabs {
    margin-inline: -15px;
    background: #3870FF;
    border-radius: 20px 20px 0 0;
    color: #fff;
    flex: 1;
    z-index: 1;
    transition: all .2s ease;

    &--active {
      border-top: 1px solid;
      border-radius: 1.3rem 1.3rem 0 0;
      /*border-left: 1px solid;*/
      background: #fff;
      flex: 1.1;
      color: #000;
      z-index: 2;
      margin-top: -1px;
    }
  }
}

.subcategory {
  &__list {
   // padding: 30px 50px;

  }

  &__button {
    background-color: #3870FF;
    color: #fff;
    //padding: 15px 55px;
  }

  input[type=checkbox] + label {
    border-radius: 8px;
    padding: 6px 12px;
  }

  input[type=checkbox]:checked + label {
    background: rgba(56, 112, 255, 0.1);
    border-radius: 8px;
    padding-left: 42px;
    margin-left: -30px;
  }
}
@media(max-width:768px) {
    .subcategory {
        &__list {
            //  padding: 15px;
        }
        input[type=checkbox] + label {
            padding: 8px 10px;
        }
        input[type=checkbox]:checked + label {
            margin-left: -26px;
            margin-right: 15px;
            padding-left: 33px;
        }
    }
}
.result {
  background: #fff;
}
</style>